"use client";

import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";

import { Check } from "lucide-react";

import { renderTemplate } from "@repo/ui/components/common/cms";
import Spinner from "@repo/ui/components/Extras/common/spinner";
import { Button } from "@repo/ui/components/ui/button";
import { DIRECTIONS } from "@repo/ui/constants/directions";
import { getSessionPreferences } from "@repo/ui/helpers";
import { useFlexiSmartSSR } from "@repo/ui/Hooks/useFlexiSmartSSR";
import { useFormatPrice } from "@repo/ui/Hooks/useFormatPrice";
import { getTabSessionId } from "@repo/ui/lib/tabSessionId";
import { cn } from "@repo/ui/lib/utils";
import type { FlexiSmartCardSection } from "@repo/ui/types/cms";

import { useButtonStatus } from "@repo/store/slices";

import { useExtrasContext } from "../common/ExtrasProvider";

interface FlexibilityCardProps {
  id: string;
  infoText?: ReactNode;
  title: ReactNode;
  subtitle: ReactNode;
  price: string | number;
  showButton?: boolean;
  disabledButton?: boolean;
  direction?: string;
  isCheckin?: boolean;
  className?: string;
  cmsData?: FlexiSmartCardSection;
  showPrice?: boolean;
  addedButtonText?: string;
}

export function FlexibilityCard({
  id,
  infoText,
  title,
  subtitle,
  price,
  showButton = true,
  disabledButton = false,
  direction,
  isCheckin = false,
  className,
  cmsData,
  showPrice = false,
  addedButtonText = "Agregado",
}: Readonly<FlexibilityCardProps>) {
  const buttonStatus = useButtonStatus();
  const formatPrice = useFormatPrice();
  const { isInboundClose, isOutboundClose } = useExtrasContext();

  const tabSessionId = getTabSessionId();

  const isJourneyDisabled = useMemo(
    () => (direction === DIRECTIONS.OUTBOUND ? isOutboundClose : isInboundClose),
    [direction, isInboundClose, isOutboundClose],
  );

  const cardKey = `${id}-${direction}`;
  const isAdded = buttonStatus?.flexiSmartButtonStatus?.[cardKey] ?? false;

  const [currencyCode, setCurrencyCode] = useState("CLP");

  useEffect(() => {
    const loadSession = async () => {
      const sessionProps = await getSessionPreferences(tabSessionId || "");
      setCurrencyCode(sessionProps?.currency || "CLP");
    };

    loadSession();
  }, [tabSessionId]);

  const { loading, toggleFlexiSmart } = useFlexiSmartSSR({
    tabSessionId: tabSessionId || "",
    currencyCode,
    direction: direction || "",
  });

  const formattedPrice = formatPrice(
    typeof price === "string" ? Number.parseFloat(price) : Number(price),
  );

  const toggleFlexiSmartParams = useMemo(() => {
    return { isAdded, isCheckin, price: Number(price), cardKey };
  }, [isAdded, isCheckin, price, cardKey]);

  const buttonContent = useMemo(() => {
    const baseText = isAdded
      ? addedButtonText
      : renderTemplate(cmsData?.buttonText ?? "Agregar", {});

    return (
      <span className="flex flex-row items-center gap-2">
        {baseText}
        {loading && <Spinner className="inline-flex" colorClassName="h-4 w-4 text-current" />}
        {isAdded && !loading && <Check className="h-4 w-4 text-current" />}
      </span>
    );
  }, [loading, isAdded, addedButtonText, cmsData]);

  return (
    <div
      className={cn(
        "flex h-full w-full flex-col",
        { "opacity-50": disabledButton || isJourneyDisabled },
        className,
      )}
      data-testid={`flexi-${cardKey}-card`}
    >
      {/* INFO BAR — HEIGHT RESERVED, NO BORDER */}
      <div className="min-h-12 px-3 py-2 flex items-center justify-center">
        {infoText || <span className="invisible">placeholder</span>}
      </div>

      {/* CARD BODY — BORDER STARTS HERE */}
      <div className="border-brand-cyan flex flex-1 flex-col items-center justify-center rounded-lg border p-4 text-center">
        <div className="text-brand-cyan text-sm font-bold">
          {title} {showPrice && formattedPrice}
        </div>

        <div className="mt-1 flex items-center gap-1 text-xs font-semibold">{subtitle}</div>

        {showButton && (
          <Button
            size="sm"
            variant="secondary"
            disabled={disabledButton || isJourneyDisabled || loading}
            className={cn(
              "mt-3 text-white transition-all",
              isAdded ? "bg-brand-cyan hover:bg-cyan-600!" : "hover:bg-orange-500!",
            )}
            onClick={() => toggleFlexiSmart(toggleFlexiSmartParams)}
            data-testid={`flexi-${cardKey}-card-btn`}
          >
            {buttonContent}
          </Button>
        )}
      </div>
    </div>
  );
}
